<script setup lang="ts">
import type { CellCoord, CellErrorMsg, CSVObj } from "@/types"
import { cellCoord } from "@/utils/fileImport/autolocate"
import { computed, nextTick, ref, watch } from "vue"

interface Props {
  /**
   * Table Items
   */
  table: CSVObj
  selectedHeader?: number
  invalidCells?: Map<CellCoord, CellErrorMsg>
  selectedColumnContainsMixedContent?: boolean
}

const props = defineProps<Props>()

const rowIndexOffset = computed(() => {
  if (props.table.headers) {
    return 2
  }

  return 1
})

const invalidColumns = computed(() => {
  if (props.invalidCells) {
    return new Set(
      // Coords are strings "{row},{col}"
      Array.from(props.invalidCells.keys()).map((coord) =>
        Number.parseInt(coord.split(",")[1])
      )
    )
  }

  return new Set()
})

const invalidRows = computed(() => {
  if (props.invalidCells) {
    return new Set(
      // Coords are strings "{row},{col}"
      Array.from(props.invalidCells.keys()).map((coord) =>
        Number.parseInt(coord.split(",")[0])
      )
    )
  }

  return new Set()
})

function isColumnInvalid(colIndex: number): boolean {
  return (
    invalidColumns.value.has(colIndex) ||
    props.selectedColumnContainsMixedContent
  )
}

// Only highlight ignored rows if the selected column contains an invalid cell.
function isRowIgnored(rowIndex: number): boolean {
  if (!props.selectedHeader) {
    return false
  }

  return (
    isColumnInvalid(props.selectedHeader) && invalidRows.value.has(rowIndex)
  )
}

function isCellInvalid(rowIndex: number, colIndex: number): boolean {
  // The actual message isn't really used yet. We may want to put it in a tool tip or similar at some point?
  const invalidMsg = props.invalidCells?.get(cellCoord(rowIndex, colIndex))
  return invalidMsg !== undefined
}

const tableRef = ref()

watch(
  () => props.selectedHeader,
  async () => {
    await nextTick()
    const column = tableRef.value.querySelector(".selectedAlphabet")

    if (column) {
      column.scrollIntoView({ behavior: "smooth", block: "end" })
    }
  }
)
</script>

<template>
  <table
    ref="tableRef"
    class="ph-no-capture table-auto select-none overflow-auto text-sm"
  >
    <thead class="sticky top-0">
      <tr v-if="table.sheetHeaders">
        <th class="border border-gray-400 bg-warmgray-100 px-2 py-1 font-bold" />
        <th
          v-for="(alphabetHeader, index) in table.sheetHeaders"
          :key="alphabetHeader"
          class="whitespace-nowrap border border-gray-400 bg-warmgray-100 px-2 py-1 font-bold"
          :class="{
            selectedAlphabet:
              index === selectedHeader && !isColumnInvalid(index),
            invalidAlphabet: index === selectedHeader && isColumnInvalid(index),
          }"
        >
          {{ alphabetHeader }}
        </th>
      </tr>
      <tr v-if="table.headers">
        <th class="border border-gray-400 bg-warmgray-100 px-2 py-1">
          1
        </th>
        <th
          v-for="(header, index) in table.headers"
          :key="header"
          class="whitespace-nowrap border border-gray-400 bg-warmgray-100 px-2 py-1 text-left font-bold"
          :class="{
            selected: index === selectedHeader && !isColumnInvalid(index),
            invalid: index === selectedHeader && isColumnInvalid(index),
          }"
        >
          {{ header }}
        </th>
      </tr>
    </thead>
    <tbody v-if="table.rows">
      <tr
        v-for="(row, rowIndex) in table.rows"
        :key="rowIndex"
      >
        <th class="border border-gray-400 bg-warmgray-100 px-2 py-1 font-bold">
          {{ rowIndex + rowIndexOffset }}
        </th>
        <td
          v-for="(cell, index) in row"
          :key="`${rowIndex}-${cell}`"
          class="min-w-32 whitespace-nowrap border border-gray-400 px-2 py-1"
          :class="{
            selected: index === selectedHeader && !isColumnInvalid(index),
            columnInvalid: index === selectedHeader && isColumnInvalid(index),
            invalid: index === selectedHeader && isCellInvalid(rowIndex, index),
            ignored: isRowIgnored(rowIndex) && !isCellInvalid(rowIndex, index),
          }"
        >
          <div>{{ cell }}</div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<style scoped>
@reference "@/style.css";

table {
  @apply mb-4 border-separate border-spacing-0 rounded-lg bg-white;
}

table th,
table td {
  @apply w-px whitespace-nowrap;
}

table thead tr:first-child th:first-child {
  @apply rounded-tl-md;
}

table tr:first-child th:last-child {
  @apply rounded-tr-md;
}

table thead tr > th.selected {
  @apply rounded-t-md border-x-2 border-t-2 border-x-teal-700 border-t-teal-700;
}

table thead tr > th.invalid {
  @apply rounded-t-md border-x-2 border-t-2 border-x-yellow-300 border-t-yellow-300;
}

table thead tr > th.selectedAlphabet {
  @apply bg-teal-100 ring-teal-100;
}

table thead tr > th.invalidAlphabet {
  @apply bg-yellow-50;
}

table > tbody > tr > td.selected {
  @apply border-x-2 border-x-teal-700;
}

table > tbody > tr > td.columnInvalid {
  @apply border-x-2 border-x-yellow-300;
}

table > tbody > tr:last-child td.selected {
  @apply rounded-b-md border-b-2 border-b-teal-700;
}

table > tbody > tr:last-child td.columnInvalid {
  @apply rounded-b-md border-b-2 border-b-yellow-300;
}

table > tbody > tr > td.invalid {
  @apply bg-yellow-50;
}

table > tbody > tr > td.ignored {
  @apply bg-gray-200;
}
</style>
